import { observable, computed, makeObservable } from 'mobx'
import { DateZero, maybeParseDate } from 'utils/date-util'
import {
  NotificationDTO,
  NotificationStatus,
} from 'notifications/dtos/notifications'
import { PrimeMember } from 'iam/MemberStore'
import { Id } from 'types/definitions'
import Member from 'iam/models/Member'
import { Model } from 'infra/model'
import link from 'infra/link'
import { stripMinidown } from 'minidown/strip'

export default class Notification extends Model<NotificationDTO, Notification> {
  key = ''
  @observable id = Id.none
  @observable message = ''
  @observable dateDispatch: Date = DateZero()
  @observable image: NotificationImage = { type: 'None' }
  @observable action: NotificationAction = { type: 'None' }
  @observable recipient = Member.none
  @observable status: NotificationStatus = 'Sent'

  constructor(private primeMember: PrimeMember) {
    super()
    makeObservable(this)
  }

  /**
   * Image url.
   */
  @computed
  get imageUrl() {
    const defaultImage = this.recipient.workspace.branding.logos.icon
    const image = this.image
    if (!image) {
      return defaultImage
    }

    switch (image.type) {
      case 'Static':
        return image.url
      case 'UserAvatar':
        return image.member.avatar || image.member.avatarFallback
      default:
        return defaultImage
    }
  }

  @computed
  get actionUrl() {
    const action = this.action
    if (!action) {
      return null
    }

    switch (action.type) {
      case 'ViewAvailableJobs':
        return link.opportunities()
      case 'ViewJob':
        return link.job(action.jobId)
      default:
        return null
    }
  }

  /**
   * The notification text without any formatting.
   */
  @computed
  get plainText() {
    return stripMinidown(this.message)
  }

  parse(attrs: Partial<NotificationDTO>): Partial<Notification> {
    return {
      id: attrs.id,
      key: attrs.key,
      message: attrs.message,
      status: attrs.status,
      action: this.parseAction(attrs),
      image: this.parseImage(attrs),
      dateDispatch: maybeParseDate(attrs.date_dispatch),
      recipient: this.primeMember({
        workspace_id: attrs.workspace_id,
        user_id: attrs.user_id,
        user_public_id: attrs.user_public_id,
      }),
    }
  }

  private parseImage(
    attrs: Partial<NotificationDTO>
  ): NotificationImage | undefined {
    if (!attrs.image) {
      return undefined
    }
    const image = attrs.image
    switch (image.type) {
      case 'Static':
        return { type: 'Static', url: image.url }
      case 'UserAvatar':
        return {
          type: 'UserAvatar',
          member:
            this.primeMember({
              workspace_id: attrs.workspace_id || this.recipient.workspace.id,
              user_id: image.user_id,
              user_public_id: image.user_public_id,
            }) || Member.none,
        }
      default:
        return { type: 'None' }
    }
  }

  private parseAction({
    action,
  }: Partial<NotificationDTO>): NotificationAction | undefined {
    if (!action) {
      return undefined
    }

    switch (action.type) {
      case 'ViewAvailableJobs':
        return { type: 'ViewAvailableJobs' }
      case 'ViewJob':
        return { type: 'ViewJob', jobId: action.job_id }
      default:
        return { type: 'None' }
    }
  }
}

export interface NoImage {
  type: 'None'
}

export interface StaticImage {
  type: 'Static'
  url: string
}

export interface UserAvatarImage {
  type: 'UserAvatar'
  member: Member
}

export declare type NotificationImage = NoImage | StaticImage | UserAvatarImage

export interface DoNothingAction {
  type: 'None'
}

export interface ViewAvailableJobsAction {
  type: 'ViewAvailableJobs'
}

export interface ViewJobAction {
  type: 'ViewJob'
  jobId: string
}

export declare type NotificationAction =
  | DoNothingAction
  | ViewJobAction
  | ViewAvailableJobsAction
